import { ButtonMenu, GridMenu } from "@/components";
import {
  MenuButton,
  SquareIconButton,
  SquareIconMenuButton,
  ToggleButton,
} from "@/components/menu/buttons";
import { WorkspaceHeaderDivider } from "@/components/menu/ui";
import { transformers, views } from "@/configs/workspace";
import {
  useActionHandler,
  useFullscreen,
  useHasDataInput,
  useTranslation,
} from "@/hooks";
import { useProcessedData } from "@/stores/data-processing";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { useSidebar, useView } from "@/stores/ui";
import { hasGeographicData } from "@/utils/data/hasGeographicData";
import { Flex } from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconBraces,
  IconClipboard,
  IconDatabaseSearch,
  IconDatabaseStar,
  IconDeviceFloppy,
  IconFileTypeXls,
  IconBoxPadding,
  IconLayersSelected,
  IconMapPin,
  IconPackage,
  IconSettings,
  IconShare2,
  IconShare3,
  IconTable,
  IconTransform,
  IconUpload,
  IconWand,
} from "@tabler/icons-react";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import "./WorkspaceHeader.css";

export function WorkspaceHeader() {
  const { t } = useTranslation();
  const view = useView();
  //const dataflowId = useWorkspaceDataflowId();
  /*
  const mapModel = useUIStore((state) => {
    const workspaceUI = state.getWorkspaceUI(dataflowId);
    return workspaceUI?.mapModel || "stadia";
  });
  */
  const sidebar = useSidebar();
  const actionHandler = useActionHandler();
  const navigate = useNavigate();
  const isFullscreen = useFullscreen();
  const hasDataInput = useHasDataInput();
  const activeDataflowId = useActiveDataflowId();
  const processedData = useProcessedData(activeDataflowId || "");

  // Check if data has geographic data (lat/lng) for Groups button
  const hasGeographicDataCheck = processedData?.metadata?.columns
    ? hasGeographicData(processedData.data, processedData.metadata.columns)
        .hasGeographicData
    : false;

  const isDisabled = false;
  const isDatasetsActive = sidebar.mode === "discover" && sidebar.isOpen;
  const isFlowActive = sidebar.mode === "flow" && sidebar.isOpen;
  const isLayersActive = sidebar.mode === "layers" && sidebar.isOpen;
  const viewConfig = useMemo(() => views[view] || views.map, [view]);
  //const mapModelConfig = useMemo(() => maps[mapModel], [mapModel]);
  //const isSaving = useIsSavingActive();

  const workspaceTransformers = useMemo(
    () =>
      Object.values(transformers)
        .filter((transformer) => transformer.id !== "unknown")
        .map((transformer) => (
          <SquareIconMenuButton
            key={transformer.id}
            id={transformer.id}
            tooltip={transformer.tooltip}
            icon={transformer.icon}
            label={transformer.label}
            onClick={actionHandler}
            disabled={!hasDataInput}
          />
        )),
    [transformers, hasDataInput, actionHandler]
  );

  const workspaceViews = useMemo(
    () =>
      Object.values(views)
        .filter((workspaceView) => !workspaceView.disabled)
        .map((workspaceView) => (
          <SquareIconMenuButton
            key={workspaceView.id}
            id={workspaceView.id}
            tooltip={workspaceView.tooltip}
            icon={workspaceView.icon}
            label={workspaceView.label}
            onClick={actionHandler}
            isActive={view === workspaceView.id}
          />
        )),
    [views, actionHandler, view]
  );

  const textLayersVisible = useMapStore((state) => state.textLayersVisible);
  const roadsLayersVisible = useMapStore((state) => state.roadsLayersVisible);
  const dataLabelsVisible = useMapStore((state) => state.dataLabelsVisible);

  const workspaceMapOptions = useMemo(
    () => (
      <>
        <SquareIconButton
          id="layers"
          tooltip={t("workspace").mapLayers}
          icon={IconLayersSelected}
          label={t("workspace").layers}
          onClick={actionHandler}
          disabled={isDisabled}
          isActive={isLayersActive}
        />
        <ButtonMenu
          id="map"
          tooltip={t("workspace").mapFeatures}
          icon={IconBoxPadding}
          label={t("workspace").features}
          disabled={isDisabled}
        >
          <ToggleButton
            id="toggle_text_layers"
            label={t("workspace").textLayers}
            onClick={actionHandler}
            isActive={textLayersVisible}
          />
          <ToggleButton
            id="toggle_roads_layers"
            label={t("workspace").roadsLayers}
            onClick={actionHandler}
            isActive={roadsLayersVisible}
          />
          <ToggleButton
            id="toggle_data_labels"
            label={t("workspace").dataLabels}
            onClick={actionHandler}
            isActive={dataLabelsVisible}
          />
        </ButtonMenu>
      </>
    ),
    [
      t,
      isDisabled,
      actionHandler,
      isLayersActive,
      hasGeographicDataCheck,
      textLayersVisible,
      roadsLayersVisible,
      dataLabelsVisible,
    ]
  );

  const workspaceOptions = useMemo(() => {
    switch (view) {
      case "map":
        return workspaceMapOptions;
      default:
        return null;
    }
  }, [view, workspaceMapOptions]);

  function handleBack() {
    navigate("/build");
  }

  function handleImport() {
    navigate("/my-data");
  }

  return (
    <header className="workspace-header">
      <Flex h="100%" gap={2}>
        {/* Navigation Section */}
        <SquareIconButton
          id="back"
          tooltip={t("workspace").backToProjects}
          icon={IconArrowLeft}
          label={t("workspace").back}
          onClick={handleBack}
          disabled={isDisabled}
        />
        <WorkspaceHeaderDivider />

        {/* Data Operations */}
        <SquareIconButton
          id="datasets"
          tooltip={t("workspace").bookmarkedDatasets}
          icon={IconDatabaseSearch}
          label={t("workspace").datasets}
          onClick={actionHandler}
          disabled={isDisabled}
          isActive={isDatasetsActive}
        />
        <SquareIconButton
          id="upload"
          tooltip={t("workspace").importDataset}
          icon={IconUpload}
          label={t("workspace").import}
          onClick={handleImport}
          disabled={isDisabled}
        />
        <SquareIconButton
          id="paste"
          tooltip={t("workspace").pasteFromClipboard}
          icon={IconClipboard}
          label={t("workspace").clipboard}
          onClick={actionHandler}
          disabled={isDisabled}
        />
        <WorkspaceHeaderDivider />

        {/* Transform Operations */}
        <SquareIconButton
          id="run"
          tooltip={t("workspace").configureFlow}
          icon={IconSettings}
          label={t("workspace").configure}
          onClick={actionHandler}
          disabled={isDisabled}
          isActive={isFlowActive}
        />
        <GridMenu
          id="transform"
          tooltip={t("workspace").transformDataset}
          icon={IconTransform}
          label={t("workspace").transform}
          disabled={isDisabled || !hasDataInput}
        >
          {workspaceTransformers}
        </GridMenu>
        <GridMenu
          id="enhance"
          tooltip={t("workspace").enhanceDataset}
          icon={IconWand}
          label={t("workspace").enhance}
          disabled={isDisabled || !hasDataInput}
        >
          <SquareIconMenuButton
            id="geocode"
            tooltip={t("workspace").geocodeAddresses}
            icon={IconMapPin}
            label={t("workspace").geocode}
            onClick={actionHandler}
            disabled={false}
          />
          <SquareIconMenuButton
            id="service1"
            tooltip={t("workspace").service1}
            icon={IconPackage}
            label={t("workspace").service1}
            onClick={actionHandler}
            disabled={true}
          />
          <SquareIconMenuButton
            id="service2"
            tooltip={t("workspace").service2}
            icon={IconPackage}
            label={t("workspace").service2}
            onClick={actionHandler}
            disabled={true}
          />
        </GridMenu>

        <WorkspaceHeaderDivider />

        {/* View Selection */}
        <GridMenu
          id="view"
          tooltip={t("workspace").selectView}
          icon={viewConfig.icon}
          label={viewConfig.label}
          disabled={isDisabled}
        >
          {workspaceViews}
        </GridMenu>

        {/* View-specific buttons */}
        {workspaceOptions}
      </Flex>

      {/* Right Section */}
      <Flex h="100%">
        <SquareIconButton
          id="expand"
          tooltip={
            isFullscreen
              ? t("workspace").exitFullscreen
              : t("workspace").enterFullscreen
          }
          icon={isFullscreen ? IconArrowsMinimize : IconArrowsMaximize}
          label={isFullscreen ? t("workspace").exit : t("workspace").expand}
          onClick={actionHandler}
          disabled={isDisabled}
        />
        <ButtonMenu
          id="save"
          tooltip="Save Dataset"
          icon={IconDeviceFloppy}
          label="Save"
          disabled={
            isDisabled || !processedData || processedData.data.length === 0
          }
        >
          <MenuButton
            id="save_to_my_data"
            icon={IconDatabaseStar}
            label="Save to My Data..."
            onClick={actionHandler}
          />
        </ButtonMenu>
        <SquareIconButton
          id="share"
          tooltip={t("workspace").shareDataflow}
          icon={IconShare3}
          label={t("workspace").share}
          onClick={actionHandler}
          disabled={isDisabled}
        />
        <ButtonMenu
          id="export"
          tooltip={t("workspace").exportDataflow}
          icon={IconShare2}
          label={t("workspace").export}
          disabled={isDisabled}
        >
          <MenuButton
            id="json"
            icon={IconBraces}
            label={t("workspace").json}
            onClick={actionHandler}
          />
          <MenuButton
            id="csv"
            icon={IconTable}
            label={t("workspace").csv}
            onClick={actionHandler}
          />
          <MenuButton
            id="excel"
            icon={IconFileTypeXls}
            label={t("workspace").excel}
            onClick={actionHandler}
          />
          <MenuButton
            id="paquet"
            icon={IconPackage}
            label={t("workspace").parquet}
            onClick={actionHandler}
          />
        </ButtonMenu>
        {/*
        <WorkspaceHeaderDivider />
        <SquareIconButton
          id="report"
          tooltip={t("workspace").reportIssue}
          icon={IconAlertTriangle}
          label={t("workspace").report}
          onClick={actionHandler}
          disabled={isDisabled}
        />
        */}
      </Flex>
    </header>
  );
}
